import { useCallback, useEffect, useState } from 'react';
import type { Deal } from '@bcc/shared';
import { DEAL_STATUSES } from '@bcc/shared';
import { Link } from 'react-router-dom';
import { SITE_FORM_CONTROL, SITE_GLASS_CARD } from '../../constants/siteUi';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { DEAL_PROPERTY_CLASS_OPTIONS, DEAL_STRATEGY_OPTIONS, DEAL_ASSET_TYPE_OPTIONS } from '../../constants/dealFormOptions';

export function AdminDeals() {
  const { session, role } = useAuth();
  const token = session?.access_token;
  const [deals, setDeals] = useState<Deal[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    const r = await apiFetch<{ deals: Deal[] }>('/api/deals', { token });
    setDeals(r.deals);
  }, [token]);

  useEffect(() => {
    void load().catch((e) => setErr((e as Error).message));
  }, [load]);

  async function patchDeal(id: string, patch: Record<string, unknown>) {
    if (!token) return;
    setErr(null);
    try {
      await apiFetch(`/api/deals/${id}`, { method: 'PATCH', token, body: JSON.stringify(patch) });
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function removeDeal(id: string) {
    if (!token) return;
    if (!window.confirm('Permanently delete this deal?')) return;
    await apiFetch(`/api/deals/${id}`, { method: 'DELETE', token });
    await load();
  }

  return (
    <div className="max-w-5xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--home-text-on-dark)]">Deal catalog</h1>
          <p className="mt-1 text-sm text-[var(--home-muted-on-dark)]">
            Archive hides deals from public pages. Featured surfaces them first on the marketing Deals page.
          </p>
        </div>
        {role === 'admin' ? (
          <Link
            to="/admin/upload-deal"
            className="rounded-xl bg-[var(--home-miami-blue)] px-4 py-2 text-sm font-semibold text-[var(--home-zone-void)] hover:brightness-105"
          >
            + New deal
          </Link>
        ) : null}
      </div>

      {err ? <p className="text-sm text-rose-300">{err}</p> : null}

      <div className={`${SITE_GLASS_CARD} overflow-hidden`}>
        <ul className="divide-y divide-white/[0.08]">
          {deals.map((d) => (
            <li key={d.id} className="px-5 py-4 text-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="font-medium text-[var(--home-text-on-dark)]">{d.title}</p>
                  <p className="text-xs text-[var(--home-muted-on-dark)]">
                    {d.status} · {d.city ?? '—'}, {d.state ?? '—'} · {d.property_type ?? '—'} · {d.property_class ?? '—'} /{' '}
                    {d.strategy ?? '—'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-1.5 text-xs text-[var(--home-text-on-dark)]">
                    <input
                      type="checkbox"
                      checked={Boolean(d.featured_on_landing)}
                      onChange={(e) => void patchDeal(d.id, { featured_on_landing: e.target.checked })}
                    />
                    Featured
                  </label>
                  <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-1.5 text-xs text-[var(--home-text-on-dark)]">
                    <input
                      type="checkbox"
                      checked={Boolean(d.archived)}
                      onChange={(e) => void patchDeal(d.id, { archived: e.target.checked })}
                    />
                    Archived
                  </label>
                  <select
                    value={d.status}
                    onChange={(e) => void patchDeal(d.id, { status: e.target.value })}
                    className={`${SITE_FORM_CONTROL} text-xs`}
                  >
                    {Object.values(DEAL_STATUSES).map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <details className="mt-3 rounded-lg border border-white/10 bg-black/15 p-3">
                <summary className="cursor-pointer text-xs font-medium text-[var(--home-miami-blue)]">Type, class &amp; strategy</summary>
                <div className="mt-3 grid gap-3 sm:grid-cols-3">
                  <label className="block text-xs">
                    <span className="text-[var(--home-muted-on-dark)]">Property type</span>
                    <select
                      value={d.property_type ?? DEAL_ASSET_TYPE_OPTIONS[0]}
                      onChange={(e) => void patchDeal(d.id, { property_type: e.target.value })}
                      className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                    >
                      {DEAL_ASSET_TYPE_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs">
                    <span className="text-[var(--home-muted-on-dark)]">Property class</span>
                    <select
                      value={d.property_class ?? DEAL_PROPERTY_CLASS_OPTIONS[0]}
                      onChange={(e) => void patchDeal(d.id, { property_class: e.target.value })}
                      className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                    >
                      {DEAL_PROPERTY_CLASS_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block text-xs">
                    <span className="text-[var(--home-muted-on-dark)]">Strategy</span>
                    <select
                      value={d.strategy ?? DEAL_STRATEGY_OPTIONS[0]}
                      onChange={(e) => void patchDeal(d.id, { strategy: e.target.value })}
                      className={`mt-1 w-full ${SITE_FORM_CONTROL}`}
                    >
                      {DEAL_STRATEGY_OPTIONS.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </details>
              <div className="mt-3 flex gap-2">
                <a href={`/deals/${d.id}`} className="text-xs text-[var(--home-miami-blue)] underline" target="_blank" rel="noreferrer">
                  View public
                </a>
                {role === 'admin' ? (
                  <button type="button" onClick={() => void removeDeal(d.id)} className="text-xs text-rose-300 hover:underline">
                    Delete
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        {deals.length === 0 ? <p className="px-5 py-8 text-sm text-[var(--home-muted-on-dark)]">No deals yet.</p> : null}
      </div>
    </div>
  );
}
